<template>
	<div class="general-settings">
		<AppCard class="card" :title="getString('business_settings')">
			<template #body>
				<AppList class="list" :items="items">
					<template #dropdown-header="{item}">
						<div @click="item.callback" class="dropdown-header row">
							<p class="list-item-title">{{ item.title }}</p>
							<AppChevronIcon :direction="item.active ? 'up' : 'down'" />
						</div>
					</template>
					<template #business-info>
						<div class="col-12 business-info-container">
							<div class="row input-button-row">
								<div class="col-grow">
									<div class="row vat-loader-row space-between pr-10">
										<p>{{ getString("vat") }}</p>
										<AppLoader v-if="loadingVat" :size="22" primary />
									</div>
								</div>
								<div>
									<div class="row centered flex-end">
										<AppTextInput
											class="single-input-wrapper"
											:readonly="loadingVat"
											:value="userStore && userStore.modBusinessInfos && userStore.modBusinessInfos.vat ? userStore.modBusinessInfos.vat : ''"
											@changed-text="changedVat"
											ref="vat-input"
											:placeholder="getString('vat_example')"
											no-autocomplete
										/>
									</div>
								</div>
							</div>
							<div class="row input-button-row">
								<p>{{ getString("fiscal_name") }}</p>
								<div>
									<div class="row centered flex-end">
										<AppTextInput
											class="single-input-wrapper"
											:readonly="loadingVat"
											:value="currentFiscalName"
											@changed-text="(value) => userStore.modBusinessInfos.fiscal_name = value"
											ref="fiscal-name-input"
											:placeholder="getString('fiscal_name_example')"
											no-autocomplete
										/>
									</div>
								</div>
							</div>
							<div class="row input-button-row">
								<p>{{ getString('legal_address') }}</p>
								<div>
									<div class="row centered flex-end">
										<AppGoogleMapsInput
											:class="{disabled: loadingVat}"
											class="input-wrapper single-input-wrapper"
											:placeholder="getString('address')"
											@changed-address="changeBusinessLegalAddress"
											:value="currBusinessLegalAddress"
											ref="legal-address-input"
											no-map
										/>
									</div>
								</div>
							</div>
							<div class="row input-button-row">
								<p>{{ getString('business_address') }}</p>
								<div>
									<div class="row centered flex-end">
										<AppGoogleMapsInput
											:class="{disabled: loadingVat}"
											class="input-wrapper single-input-wrapper"
											:placeholder="getString('address')"
											@changed-address="changeBusinessAddress"
											:value="currBusinessAddress"
											ref="address-input"
											no-map
										/>
									</div>
								</div>
							</div>
							<div class="row input-button-row">
								<p>{{ getString("fiscal_code") }}</p>
								<div>
									<div class="row centered flex-end">
										<AppTextInput
											class="single-input-wrapper"
											:readonly="loadingVat"
											:value="userStore && userStore.modBusinessInfos && userStore.modBusinessInfos.fiscal_code ? userStore.modBusinessInfos.fiscal_code : ''"
											@changed-text="(value) => userStore.modBusinessInfos.fiscal_code = value"
											ref="fiscal-code-input"
											:placeholder="getString('fiscal_code_example')"
											no-autocomplete
										/>
									</div>
								</div>
							</div>
							<div class="row input-button-row" v-if="userStore.currBusiness && userStore.currBusiness.country === 'IT'">
								<p>{{ getString("sdi") }}</p>
								<div>
									<div class="row centered flex-end">
										<AppTextInput
											class="single-input-wrapper"
											:readonly="loadingVat"
											:value="userStore && userStore.modBusinessInfos && userStore.modBusinessInfos.sdi ? userStore.modBusinessInfos.sdi : ''"
											@changed-text="(value) => userStore.modBusinessInfos.sdi = value"
											ref="sdi-input"
											:placeholder="getString('sdi_example')"
											no-autocomplete
										/>
									</div>
								</div>
							</div>
							<div class="row input-button-row">
								<p>{{ getString("company_email") }}</p>
								<div>
									<div class="row centered flex-end">
										<AppTextInput
											class="single-input-wrapper"
											:value="userStore && userStore.modBusinessInfos && userStore.modBusinessInfos.company_email ? userStore.modBusinessInfos.company_email : ''"
											@changed-text="(value) => userStore.modBusinessInfos.company_email = value"
											ref="company-email-input"
											:placeholder="getString('email_example')"
											no-autocomplete
										/>
									</div>
								</div>
							</div>
							<div class="row input-button-row" v-if="userStore.currBusiness && userStore.currBusiness.country === 'IT'">
								<p>{{ getString("pec") }}</p>
								<div>
									<div class="row centered flex-end">
										<AppTextInput
											class="single-input-wrapper"
											:value="userStore && userStore.modBusinessInfos && userStore.modBusinessInfos.email_pec ? userStore.modBusinessInfos.email_pec : ''"
											@changed-text="(value) => userStore.modBusinessInfos.email_pec = value"
											ref="pec-input"
											:placeholder="getString('pec')"
											no-autocomplete
										/>
									</div>
								</div>
							</div>
							<div class="row input-button-row">
								<p>{{ getString("company_phone") }}</p>
								<div>
									<div class="row centered flex-end">
										<AppTextInput
											class="single-input-wrapper"
											:value="userStore.modBusinessInfos && userStore.modBusinessInfos.company_phone ? userStore.modBusinessInfos.company_phone : ''"
											@changed-text="(value) => userStore.modBusinessInfos.company_phone = value"
											ref="company-phone-input"
											:placeholder="getString('phone_example')"
											no-autocomplete
										/>
									</div>
								</div>
							</div>
							<div class="row flex-end">
								<AppButton @click="saveBusinessInfos" small :text="getString('save')" />
							</div>
						</div>
					</template>
					<template #invoice-info>
						<div class="row centered space-between">
							<div class="col-12 invoice-info-container row" v-if="userStore.currBusiness && userStore.currBusiness.apifattura_id">
								<div class="row flex-end">
									<AppButton @click="disconnectApifattura" small outline :loading="syncingApifattura" :text="getString('unlink')" />
								</div>
							</div>
							<div class="col-12 invoice-info-container" v-else>
								<div class="disconnected-step-container row">
									<p v-if="!hasNecessaryFieldsForApiscontrino">{{ getString('apiscontrino_necessary_fields') }}</p>
									<div class="disconnected-step" v-else>
										<AppList :items="connectInvoicesItems" />
										<AppButton :text="getString('connect')" @click="connectApifattura" :loading="syncingApifattura" outline small />
									</div>
								</div>
							</div>
						</div>
					</template>
					<template #receipts-info="{item}">
						<div class="row receipts-info">
							<div class="connected-step row flex-end" v-if="userStore.currBusiness && userStore.currBusiness.apiscontrino_id">
								<AdeDisconnect />
							</div>
							<div class="disconnected-step-container row" v-else>
								<p v-if="!hasNecessaryFieldsForApiscontrino">{{ getString('apiscontrino_necessary_fields') }}</p>
								<div class="disconnected-step" v-else>
									<AppList :items="connectAdeItems" />
								</div>
							</div>
						</div>
					</template>
					<template #select-lang-header="{item}">
						<div class="row input-button-row select-row">
							<p>{{ item.title }}</p>
							<div class="input-btn-container">
								<div class="row centered flex-end">
									<AppSelect
										ref="lang-select"
										:values="languages"
										:selected-index="menuLanguageIndex"
										@selected="changeLanguage"
									/>
									<AppButton @click="saveLanguage" small :text="getString('save')" />
								</div>
							</div>
						</div>
					</template>
					<template #select-sound-header="{item}">
						<div class="row input-button-row select-row">
							<p>{{ item.title }}</p>
							<div class="input-btn-container">
								<div class="row centered flex-end">
									<AppSelect
										ref="lang-select"
										:values="soundsNames"
										:selected-index="soundIndex"
										@selected="changeSound"
									/>
									<AppButton @click="saveSound" small :text="getString('save')" />
								</div>
							</div>
						</div>
					</template>
					<template #select-console-appearance="{item}">
						<div class="row input-button-row select-row">
							<p>{{ item.title }}</p>
							<div class="input-btn-container">
								<div class="row centered flex-end no-margin">
									<AppSelect
										ref="console-appearance-select"
										:values="appearance"
										:selected-index="appearanceIndex"
										@selected="changeAppearance"
									/>
								</div>
							</div>
						</div>
					</template>
				</AppList>
			</template>
		</AppCard>
		<AppCard class="card card-margin" :title="getString('system_admin')">
			<template #body>
				<AppList class="list" :items="itemsSystem">
					<template #dropdown-header="{item}">
						<div @click="item.callback" class="dropdown-header row">
							<p class="list-item-title">{{ item.title }}</p>
							<AppChevronIcon :direction="item.active ? 'up' : 'down'" />
						</div>
					</template>
					<template #delete-business>
						<div class="row input-button-row">
							<p class="delete-info">{{ getString('pw_to_delete') }}</p>
							<div>
								<div class="row centered flex-end">
									<AppTextInput type="password" ref="password-input" :placeholder="getString('password')" />
									<AppButton @click="deleteBusiness" small :text="getString('delete')" />
								</div>
							</div>
						</div>
					</template>
					<template #select-integration-header="{item}">
						<div class="row input-button-row select-row">
							<p>{{ item.title }}</p>
							<div class="input-btn-container">
								<div class="row centered flex-end">
									<AppSelect
										ref="integration-select"
										:values="integrations"
										:selected-index="integrationIndex"
										@selected="changeIntegration"
									/>
									<AppButton @click="saveIntegration" small :text="getString('save')" />
								</div>
							</div>
							<div v-if="integrationIndex === 2" class="col-12 pt-5 centered align-end">
								<p class="integration-info">{{getString('integration_code', [currBusinessId])}}</p>
							</div>
						</div>
					</template>
					<template #permission-list>
						<AppCard alpha no-header inset>
							<template #body>
								<div v-show="!usersPermissions || !usersPermissions.length || loadingPermissions" class="row centered p-20">
									<AppLoader primary />
								</div>
								<PermissionsEditor
									v-show="usersPermissions && usersPermissions.length && !loadingPermissions"
									@add-user-permission="addUserPermission"
									@open-add-user-permission="toggleDisabledSaveBtn"
									ref="users-permissions-list"
									:users-permissions="usersPermissions" />
							</template>
						</AppCard>
						<div v-show="usersPermissions && usersPermissions.length && !loadingPermissions" class="row centered flex-end mt-10">
							<AppButton :disabled="disabledSavePermissions" @click="saveUsersPermission" small :text="getString('save')" />
						</div>
					</template>
					<template #webhooks-list>
						<WebhooksList />
					</template>
				</AppList>
			</template>
		</AppCard>
        <AppBottomSheet :force-width="bottomSheetWidth" :active="openLogs"  @toggling-popup="toggleLogs" >
            <BusinessLogs v-if="openLogs" />
        </AppBottomSheet>
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppList from "@/shared/components/AppList.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppCheckbox from "@/shared/components/AppCheckbox.vue";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import PermissionsEditor from "@/console-new/components/config/settings/general/PermissionsEditor.vue";

import {useOrdersStore, useUnchangingStore, useUserStore} from "@/console-new/stores";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {validateEmail} from "@/shared/helpers/strings";
import API from "@/shared/helpers/API";
import WebhooksList from "@/console-new/components/config/settings/general/WebhooksList.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import BusinessLogs from "@/console-new/components/config/settings/general/BusinessLogs.vue";
import {getLocalDateFromServer} from "@/shared/helpers/dates";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AdeDisconnect from "@/console-new/components/config/settings/general/AdeDisconnect.vue";

export default {
	name: "GeneralSettingsContainer",
	components: {
		AdeDisconnect,
		AppSwitch,
        AppBottomSheet, BusinessLogs,
        AppCheckbox, AppCard, AppList, AppLoader, PermissionsEditor, AppChevronIcon, AppTextInput, AppSelect, AppButton, AppChecklist, AppGoogleMapsInput, WebhooksList},
    mixins: [WindowWidth],
	setup() {
		let unchangingStore = useUnchangingStore();
		let userStore = useUserStore();
		let notificationStore = useNotificationStore();
		let languageStore = useLanguageStore();
		return {validateEmail, getString: languageStore.getString, languageStore, userStore, unchangingStore, showNotification: notificationStore.showNotification};
	},
	data() {
		return {
			companiesSelect: [],
			usersPermissions: [],
			permissionsValues: [
				'orders_creation',
				'orders_edit',
				'orders_pay',
				'orders_discounts',
				'orders_daily_closing',
				'analytics',
				'menu',
				'admin'
			],
			loadingGoToStripeBtn: false,
			loadingPermissions: false,
			permissionsBusinessId: null,
			disabledSavePermissions: false,
			addressChanged: false,
			legalAddressChanged: false,
			delegated: true,
			integrations: [this.getString('no_app'), 'Deliverect'],
			appearance: [this.getString('light'), this.getString('dark'), this.getString('automatic')],
			appearanceIndex: 0,
			activeBusinessInfos: false,
			activeInvoiceInfos: false,
			activeReceiptsInfos: false,
			activePermissions: false,
			activeWebhooks: false,
			activeDeleteBusiness: false,
            openLogs: false,
			sendSdi: true,
			prefixNumeration: "",
			tempApiscontrinoLogin: "",
			tempApiscontrinoPassword: "",
			tempApiscontrinoPin: "",
			loadingAde: false,
			syncingApifattura: false,
			loadingVat: false,
			sounds: ["bell_1", "bell_2", "door_bell", "chicken", "alarm_clock", "notification_1", "notification_2", "notification_3", "notification_4", "notification_5", "none"]
		}
	},
	computed: {
		connectInvoicesItems() {
			return [
				{
					title: this.getString('xml_only'),
					explain: this.getString('xml_only_explain'),
					switch: true,
					active: this.sendSdi,
					callback: (v) => this.sendSdi = v,
				},
				{
					title: this.getString('prefix_numeration'),
					explain: this.getString('prefix_numeration_explain'),
					placeholder: this.getString('prefix_numeration'),
					textInput: true,
					callbackText: (v) => this.prefixNumeration = v,
				},
			];
		},
		soundsNames() {
			return this.sounds.map(sound => this.getString(sound));
		},
		hasNecessaryFieldsForApiscontrino() {
			return this.userStore.currBusiness &&
				this.userStore.currBusiness.fiscal_name &&
				this.userStore.currBusiness.legal_address_id &&
				this.userStore.currBusiness.vat;
		},
        bottomSheetWidth() {
            if (this.windowWidth < 550) {
                return "100%";
            } else if (this.windowWidth < 768) {
                return "550px";
            } else if (this.windowWidth < 1080) {
                return "720px";
            } else if (this.windowWidth < 1440) {
                return "960px";
            } else {
                return "1280px";
            }
        },
		items() {
			let listItems = [
				{title: this.getString('business_infos'), customSlot: 'dropdown-header', dropdownSlot: 'business-info', explain: this.getString("business_infos_explain"), active: this.activeBusinessInfos, callback: () => { let curr = this.activeBusinessInfos; this.closeListDropdownHeaders(); this.activeBusinessInfos = !curr; }},
			];
			if ( this.userStore.currBusiness && this.userStore.currBusiness.country === "IT" ) {
				listItems.push({title: this.getString('invoice_infos'), customSlot: 'dropdown-header', dropdownSlot: 'invoice-info', explain: this.getString('invoices_infos_explain'), active: this.activeInvoiceInfos, callback: () => { let curr = this.activeInvoiceInfos; this.closeListDropdownHeaders(); this.activeInvoiceInfos = !curr; }});
				listItems.push({title: this.getString('receipts_infos'), customSlot: 'dropdown-header', dropdownSlot: 'receipts-info', explain: this.getString('receipts_infos_explain'), active: this.activeReceiptsInfos, callback: () => { let curr = this.activeReceiptsInfos; this.closeListDropdownHeaders(); this.activeReceiptsInfos = !curr; }});
			}
			listItems.push(
				{title: this.getString('main_lang'), customSlot: 'select-lang-header'},
			);
			listItems.push(
				{title: this.getString('notification_sound'), customSlot: 'select-sound-header'},
			);
			listItems.push(
				{title: this.getString('console_appearance'), customSlot: 'select-console-appearance'},
			);
			return listItems;
		},
		connectAdeItems() {
			return [
				{title: this.getString('ade_login'), explain: this.getString('ade_login_explain'), textInput: true, placeholder: this.getString('ade_login'), callbackText: (v) => this.tempApiscontrinoLogin = v},
				{title: this.getString('ade_password'), explain: this.getString('credentials_ade_explain'), textInput: true, placeholder: this.getString('ade_password'), inputType: 'password', callbackText: (v) => this.tempApiscontrinoPassword = v},
				{title: this.getString('ade_pin'), explain: this.getString('credentials_ade_explain'), textInput: true, placeholder: this.getString('ade_pin'), inputType: 'password', callbackText: (v) => this.tempApiscontrinoPin = v},
				{title: this.getString('delegated'), explain: this.getString('delegated_explain'), switch: true, active: this.delegated, callback: (v) => this.delegated = v},
				{title: '', buttonTitle: this.getString('connect'), callbackButton: this.connectAde, buttonLoading: this.loadingAde},
			]
		},
		itemsSystem() {
			return [
				{
					title: this.getString('permissions'),
					customSlot: 'dropdown-header',
					dropdownSlot: 'permission-list',
					active: this.activePermissions,
					callback: () => {
						let curr = this.activePermissions;
						this.clickListDropdownHeaderPermissions();
						this.activePermissions = !curr;
					}
				},
				{
					title: this.getString('webhooks'),
					customSlot: 'dropdown-header',
					dropdownSlot: 'webhooks-list',
					explain: this.getString("webhooks_explain"),
					active: this.activeWebhooks,
					callback: () => {
						let curr = this.activeWebhooks;
						this.closeListDropdownHeaders();
						this.activeWebhooks = !curr;
					}
				},
				/*{title: this.getString('external_integrations'), customSlot: 'select-integration-header'},*/
				{
					title: this.getString('business_logs'),
					buttonTitle: this.getString('logs'),
					callbackButton: () => {
						this.openLogs = true;
					}
				},
				{
					title: this.getString('delete_business'),
					customSlot: 'dropdown-header',
					dropdownSlot: 'delete-business',
					active: this.activeDeleteBusiness,
					callback: () => {
						let curr = this.activeDeleteBusiness;
						this.closeListDropdownHeaders();
						this.activeDeleteBusiness = !curr;
					}
				},
			]
		},
		currentFiscalName() {
			if(this.userStore.modBusinessInfos && this.userStore.modBusinessInfos.fiscal_name) {
				return this.userStore.modBusinessInfos.fiscal_name;
			}
			return '';
		},
		languages() {
			return this.unchangingStore.languages.map(lang => lang.language_nativeName);
		},
		currBusinessId() {
			return this.userStore.currBusinessId;
		},
		menuLanguageIndex() {
			let langs = Object.assign([], this.unchangingStore.languages);
			let foundLanguage = null;
			if(this.userStore.currBusiness && this.userStore.currBusiness.menu_language_id) {
				foundLanguage = langs.find(lang => Number(lang.language_id) === Number(this.userStore.currBusiness.menu_language_id));
			}
			return foundLanguage ? langs.indexOf(foundLanguage) : null;
		},
		soundIndex() {
			let foundSound = -1;
			if(this.userStore.currBusiness && this.userStore.currBusiness.notification_sound) {
				foundSound = this.sounds.indexOf(this.userStore.currBusiness.notification_sound);
			}
			return foundSound !== -1 ? foundSound : this.sounds.length - 1;
		},
		integrationIndex() {
			let foundIntegration = this.userStore.currBusiness && this.userStore.currBusiness.integration_id ? Number(this.userStore.currBusiness.integration_id) : null
			return foundIntegration ? foundIntegration : null;
		},
		chosenIntegration() {
			if(this.integrationIndex === 1) {
				return 'siriotrade';
			} else if(this.integrationIndex === 2) {
				return 'deliverect';
			} else {
				return null;
			}
		},
		currBusinessAddress() {
			return this.userStore.currBusiness && this.userStore.modBusinessInfos.address && this.userStore.modBusinessInfos.address.address ? this.userStore.modBusinessInfos.address.address : '';
		},
		currBusinessLegalAddress() {
			return this.userStore.modBusinessInfos && this.userStore.modBusinessInfos.legal_address && this.userStore.modBusinessInfos.legal_address.address ? this.userStore.modBusinessInfos.legal_address.address : '';
		},
	},
	methods: {
		changeAppearance(index) {
			this.appearanceIndex = index;
			this.saveAppearance();
			this.userStore.initDarkMode();
		},
		saveAppearance() {
			localStorage.setItem('darkMode', (this.appearanceIndex || 0).toString());
		},
		connectAde() {
			if(!this.tempApiscontrinoLogin || !this.tempApiscontrinoPassword || !this.tempApiscontrinoPin ||
				this.tempApiscontrinoLogin.length < 6 || this.tempApiscontrinoPassword.length < 5 || this.tempApiscontrinoPin.length < 6) {
				this.showNotification('error-ade-fields');
				return;
			}
			this.loadingAde = true;
			API.init().connectAde(this.userStore.currBusinessId, this.tempApiscontrinoLogin, this.tempApiscontrinoPassword, this.tempApiscontrinoPin, this.delegated, (r) => {
				this.loadingAde = false;
				if(r.result && r.result === "OK") {
					this.showNotification('ade-connected');
					this.userStore.currBusiness.apiscontrino_id = r.apiscontrino_id;
				} else {
					if(r.details && r.details === "failed_ade_auth") {
						this.showNotification('failed-ade-login');
					} else {
						this.showNotification('generic-fail');
					}
				}
			});
		},
		connectApifattura() {
			this.syncingApifattura = true;
			API.init().connectApifattura(this.userStore.currBusinessId, this.prefixNumeration, this.sendSdi, (r) => {
				this.syncingApifattura = false;
				if(r.result && r.result === "OK") {
					this.showNotification('generic-success');
					this.userStore.currBusiness.apifattura_id = r.apifattura_id;
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		disconnectApifattura() {
			this.syncingApifattura = true;
			API.init().disconnectApifattura(this.userStore.currBusinessId, (r) => {
				this.syncingApifattura = false;
				if(r.result && r.result === "OK") {
					this.showNotification('generic-success');
					this.userStore.currBusiness.apifattura_id = null;
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		toggleLogs(isOpen) {
			if(!isOpen) {
				this.openLogs = false;
			}
		},
		toggleDisabledSaveBtn(disable) {
			this.disabledSavePermissions = disable;
		},
		getUsersPermissions() {
			if(this.permissionsBusinessId) {
				return
			}
			this.permissionsBusinessId = this.userStore.currBusinessId;
			this.usersPermissions = [];
			let finalPermissions = [];
			this.userStore.getBusinessUsers(perms=>{
				if(this.permissionsBusinessId === this.userStore.currBusinessId) {
					this.loadingPermissions = false;
					for(let userId in perms) {
						let permissionItems = [{
							id: 'permissions',
							user_id: userId,
							title: this.getString('select_permissions'),
							customSlot: 'checklist-header',
						}, {
							id: 'expiration',
							user_id: userId,
							title: this.getString('permissions_expiration'),
							dropdownSlot: 'expiry-dropdown',
							customSlot: 'switch',
							switch: true,
							active: !!perms[userId].expires,
							expires: perms[userId].expires ? getLocalDateFromServer(perms[userId].expires) : null
						}, {
							id: 'station',
							title: this.getString('is_user_station'),
							switch: true,
							active: perms[userId].station,
							dropdownSlot: 'list-slot',
							subitems: [{
								id: 'require_code',
								title: this.getString('require_code'),
								active: perms[userId].require_code,
								switch: true,
							}]
						}];
						let codeItem = {
							id: 'code',
							title: this.getString('personal_code'),
							active: perms[userId].code,
							textInput: true,
							placeholder: this.getString('code'),
							explain: this.getString('personal_code_explain'),
							value: perms[userId].code,
						}
						codeItem.callbackText = (v) => {
							v = v.replace(/[^1-9]/g, '');
							if(v.length > 6) {
								v = v.substring(0, 6);
							}
							codeItem.value = v;
						};
						permissionItems.push(codeItem);
						finalPermissions.push({
							title: perms[userId].email,
							user_id: userId,
							customSlot: 'dropdown-header',
							dropdownSlot: 'permission-item',
							active: false,
							permissionItems: permissionItems,
						});
					}
					let permissionItems = [{
						id: 'email',
						user_id: 'add',
						title: this.getString('email'),
						customSlot: 'add-user-email'
					}, {
						id: 'permissions',
						user_id: 'add',
						title: this.getString('select_permissions'),
						customSlot: 'checklist-header'
					}, {
						id: 'expiration',
						user_id: 'add',
						title: this.getString('permissions_expiration'),
						dropdownSlot: 'expiry-dropdown',
						customSlot: 'switch',
						switch: true
					}, {
						id: 'add',
						user_id: 'add',
						title: '',
						customSlot: 'add-user-btn',
					}];
					finalPermissions.push({
						title: this.getString('new_user'),
						user_id: 'add',
						customSlot: 'dropdown-header',
						dropdownSlot: 'permission-item',
						active: false,
						disabled: true,
						permissionItems: permissionItems,
					});
					this.usersPermissions = finalPermissions;
					this.permissionsBusinessId = null;
				}
				else {
					this.permissionsBusinessId = null;
					this.getUsersPermissions();
				}
			});
		},
		closeListDropdownHeaders() {
			this.activeBusinessInfos = false;
			this.activeInvoiceInfos = false;
			this.activeReceiptsInfos = false;
			this.activePermissions = false;
			this.activeWebhooks = false;
			this.activeDeleteBusiness = false;
		},
		clickListDropdownHeaderPermissions() {
			this.closeListDropdownHeaders();
			if(!this.usersPermissions || !this.usersPermissions.length) {
				this.getUsersPermissions();
			}
		},
		saveLanguage() {
			let currSelectedLanguage = this.userStore.currBusiness ? this.userStore.currBusiness.menu_language_id : null;
			if(currSelectedLanguage === null || currSelectedLanguage === undefined) {
				this.showNotification('select-language');
				return;
			}
			API.init().updateBusiness(this.userStore.currBusinessId, {menu_language_id: currSelectedLanguage}, response => {
				if (response && response.result === 'OK') {
					this.showNotification('saved-language');
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		saveSound() {
			let currSelectedSound = this.userStore.currBusiness ? this.userStore.currBusiness.notification_sound : null;
			if(currSelectedSound === "none") {
				currSelectedSound = null;
			}
			API.init().updateBusiness(this.userStore.currBusinessId, {notification_sound: currSelectedSound}, response => {
				if (response && response.result === 'OK') {
					const ordersStore = useOrdersStore();
					ordersStore.audioNewOrder = null;
					this.showNotification('generic-success');
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		saveIntegration() {
			API.init().updateIntegration(this.userStore.currBusinessId, this.chosenIntegration, response => {
				if (response && response.result === 'OK') {
					this.showNotification('saved-integration');
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		deleteBusiness() {
			let password = this.$refs['password-input'].currentValue;
			if(!password || password.length < 6) {
				this.showNotification('error-short-password')
			} else {
				API.init().deleteBusiness(this.userStore.currBusinessId, password, (response) => {
					if (response && response.result === 'OK') {
						if(this.$refs && this.$refs['password-input']) {
							this.$refs['password-input'].currentValue = '';
						}
						this.showNotification('deleted-business');
						this.userStore.refreshUserAccount();
					} else if(response.details === 'wrong_password') {
						this.showNotification('wrong-password');
					} else if(response.details === 'business_subscribed') {
						this.showNotification('error-delete-business-subscribed');
					} else if(response.details === 'business_has_orders') {
						this.showNotification('error-delete-business-orders');
					} else {
						this.showNotification('generic-fail');
					}
				});
			}
		},
		saveUsersPermission() {
			this.loadingPermissions = true;
			let usersPermissions = {};
			for(let i = 0; i < this.usersPermissions.length - 1; i++) {
				let currUser = this.usersPermissions[i];
				usersPermissions[currUser.user_id] = this.$refs['users-permissions-list'].getUserPermission(currUser);
			}
			if(!this.userStore?.currSubscription || ['free', 'basic', 'pro'].includes(this.userStore?.currSubscription)) {
				let maxUsers = this.userStore?.currSubscription === 'pro' ? 10 : 3;
				maxUsers = this.userStore?.currSubscription === 'free' ? 1 : maxUsers;
				let usersCount = 0;
				if(usersPermissions) {
					for(let userId in usersPermissions) {
						if(usersPermissions[userId].permission && usersPermissions[userId].permission.length) {
							usersCount++;
						}
					}
				}
				if(usersCount > maxUsers) {
					this.loadingPermissions = false;
					this.showNotification('max-users-edit', [maxUsers]);
					return;
				}
			}
			API.init().updateBusinessUsers(this.userStore.currBusinessId, usersPermissions, r => {
				if (r && r.result === "OK") {
					this.userStore.refreshUserAccount(()=>{
						this.showNotification('saved-permissions');
						this.getUsersPermissions();
					});
				} else if (r && r.details === "no_admin") {
					this.loadingPermissions = false;
					this.showNotification('error-no-admin');
				} else {
					this.loadingPermissions = false;
					this.showNotification('generic-fail');
				}
			});
		},
		addUserPermission(addingUserInfo) {
			let usersPermissions = {};
			for(let i = 0; i < this.usersPermissions.length - 1; i++) {
				let currUser = this.usersPermissions[i];
				usersPermissions[currUser.user_id] = this.$refs['users-permissions-list'].getUserPermission(currUser);
			}
			if(!this.userStore?.currSubscription || ['free', 'basic', 'pro'].includes(this.userStore?.currSubscription)) {
				let maxUsers = this.userStore?.currSubscription === 'pro' ? 10 : 3;
				maxUsers = (!this.userStore?.currSubscription || this.userStore?.currSubscription === 'free') ? 1 : maxUsers;
				let usersCount = 0;
				if(usersPermissions) {
					for(let userId in usersPermissions) {
						if(usersPermissions[userId].permission && usersPermissions[userId].permission.length) {
							usersCount++;
						}
					}
				}
				if(usersCount + 1 > maxUsers) {
					this.showNotification('max-users-edit', [maxUsers]);
					return;
				}
			}
			this.loadingPermissions = true;
			API.init().addBusinessUser(this.userStore.currBusinessId, addingUserInfo.email, addingUserInfo.permission, addingUserInfo.expires, r => {
				if (r && r.result === "OK") {
					let msgBody = r.type === 'added' ? 'saved-published' : 'user-invited';
					this.showNotification(msgBody);
					this.userStore.refreshUserAccount(()=>{
						this.getUsersPermissions();
						this.disabledSavePermissions = false;
					});
				} else {
					this.loadingPermissions = false;
					let msgBody = 'generic-fail';
					if (r && r.details) {
						switch (r.details) {
							case "no_user": msgBody = 'no-user'; break;
							case "no_permissions": msgBody = 'no-permissions'; break;
							case "already_has_permissions": msgBody = 'already-has-permissions'; break;
							case "self_user": msgBody = 'self-user'; break;
							case "email_fail": msgBody = 'error-email-send'; break;
							case "already_signing_up_as_business": msgBody = 'already-in-users-temp-permission-warn'; break;
							default: msgBody = 'generic-fail';
						}
					}
					this.showNotification(msgBody);
				}
			});
		},
		saveBusinessAddress() {
			if(this.addressChanged && this.userStore.modBusinessInfos.address && this.$refs['address-input'].$refs['text-input'].currentValue) {
				API.init().addNewAddress(this.userStore.currBusinessId, this.currBusinessAddress, this.userStore.modBusinessInfos.address.lat, this.userStore.modBusinessInfos.address.lng, JSON.stringify(this.userStore.modBusinessInfos.address), "executive", response => {
					if (response && response.result === 'OK') {
						if(response.address_id) {
							this.userStore.currBusiness.address_id = Number(response.address_id);
							this.userStore.modBusinessInfos.address.address_id = Number(response.address_id);
							this.userStore.currBusiness.address = this.userStore.modBusinessInfos.address;
						}
						this.showNotification('saved-business-address');
					} else {
						this.showNotification('error-address');
					}
					this.addressChanged = false;
				});
			}
			if(this.legalAddressChanged && this.userStore.modBusinessInfos.legal_address && this.$refs['legal-address-input'].$refs['text-input'].currentValue) {
				API.init().addNewAddress(this.userStore.currBusinessId, this.currBusinessLegalAddress, this.userStore.modBusinessInfos.legal_address.lat, this.userStore.modBusinessInfos.legal_address.lng, JSON.stringify(this.userStore.modBusinessInfos.legal_address), "legal", response => {
					if (response && response.result === 'OK') {
						if(response.address_id) {
							this.userStore.currBusiness.legal_address_id = Number(response.address_id);
							this.userStore.modBusinessInfos.legal_address.address_id = Number(response.address_id);
							this.userStore.currBusiness.legal_address = this.userStore.modBusinessInfos.legal_address;
						}
						this.showNotification('saved-business-address');
					} else {
						this.showNotification('error-address');
					}
					this.legalAddressChanged = false;
				});
			}
		},
		saveFiscalData() {
			let dataUpdate = {};
			let shouldUpdate = false;

			if ((this.userStore.currBusiness.country === "IT" && !this.userStore.modBusinessInfos.sdi) ||
				//!this.userStore.modBusinessInfos.email_pec ||
				!this.userStore.modBusinessInfos.vat ||
				!this.userStore.modBusinessInfos.fiscal_name ||
				//!this.userStore.modBusinessInfos.fiscal_code ||
				//!this.userStore.modBusinessInfos.company_phone ||
				!this.userStore.modBusinessInfos.company_email
			) {
				this.showNotification('error-fill-fields');
				return;
			}

			let emailPec = this.userStore.modBusinessInfos.email_pec;
			if (emailPec) {
				if (!this.validateEmail(emailPec)) {
					this.showNotification('wrong-email');
					return;
				}
				dataUpdate.email_pec = emailPec;
				shouldUpdate = true;
			}
			if(this.userStore.modBusinessInfos.sdi && this.userStore.modBusinessInfos.sdi.length > 4 && this.userStore.modBusinessInfos.sdi.length < 10) {
				dataUpdate.sdi = this.userStore.modBusinessInfos.sdi;
				shouldUpdate = true;
			}
			if(this.userStore.modBusinessInfos.vat && this.userStore.modBusinessInfos.vat.length > 8 && this.userStore.modBusinessInfos.vat.length < 28) {
				dataUpdate.vat = this.userStore.modBusinessInfos.vat;
				shouldUpdate = true;
			}
			if(this.userStore.modBusinessInfos.fiscal_name && this.userStore.modBusinessInfos.fiscal_name.length > 3 && this.userStore.modBusinessInfos.fiscal_name.length < 255) {
				dataUpdate.fiscal_name = this.userStore.modBusinessInfos.fiscal_name;
				shouldUpdate = true;
			}
			if(!this.userStore.modBusinessInfos.fiscal_code ||
				(this.userStore.modBusinessInfos.fiscal_code && this.userStore.modBusinessInfos.fiscal_code.length > 5 && this.userStore.modBusinessInfos.fiscal_code.length < 20)) {
				dataUpdate.fiscal_code = this.userStore.modBusinessInfos?.fiscal_code || null;
				shouldUpdate = true;
			}
			if(this.userStore.modBusinessInfos.company_email && this.validateEmail(this.userStore.modBusinessInfos.company_email)) {
				dataUpdate.company_email = this.userStore.modBusinessInfos.company_email;
				shouldUpdate = true;
			}
			if(this.userStore.modBusinessInfos.company_phone && this.userStore.modBusinessInfos.company_phone.length > 4 && this.userStore.modBusinessInfos.company_phone.length < 15) {
				dataUpdate.company_phone = this.userStore.modBusinessInfos.company_phone;
				shouldUpdate = true;
			}
			if(!shouldUpdate) {
				return;
			}
			API.init().updateBusiness(this.userStore.currBusinessId, dataUpdate, response => {
				if (response && response.result === 'OK') {
					this.showNotification('saved-pec');
					if(dataUpdate.email_pec) { this.userStore.currBusiness.email_pec = this.userStore.modBusinessInfos.email_pec }
					if(dataUpdate.sdi) { this.userStore.currBusiness.sdi = dataUpdate.sdi }
					if(dataUpdate.vat) { this.userStore.currBusiness.vat = dataUpdate.vat }
					if(dataUpdate.fiscal_name) { this.userStore.currBusiness.fiscal_name = dataUpdate.fiscal_name }
					if(dataUpdate.hasOwnProperty('fiscal_code')) { this.userStore.currBusiness.fiscal_code = dataUpdate.fiscal_code }
					if(dataUpdate.company_email) { this.userStore.currBusiness.company_email = dataUpdate.company_email }
					if(dataUpdate.company_phone) { this.userStore.currBusiness.company_phone = dataUpdate.company_phone }
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		changeBusinessAddress(newAddressData) {
			let finalAddress = Object.assign({}, newAddressData, newAddressData.data);
			delete finalAddress.data;
			this.userStore.modBusinessInfos.address = this.userStore.normalizeAddress(finalAddress);
			this.userStore.modBusinessInfos.address_id = null;
			this.addressChanged = true;
		},
		changeBusinessLegalAddress(newAddressData) {
			let finalAddress = Object.assign({}, newAddressData, newAddressData.data);
			delete finalAddress.data;
			this.userStore.modBusinessInfos.legal_address = this.userStore.normalizeAddress(finalAddress);
			this.userStore.modBusinessInfos.legal_address_id = null;
			this.legalAddressChanged = true;
		},
		saveBusinessInfos() {
			this.saveFiscalData();
			this.saveBusinessAddress();
		},
		changeLanguage(languageIndex) {
			this.userStore.currBusiness.menu_language_id = this.unchangingStore.languages[languageIndex].language_id;
		},
		changeSound(soundIndex) {
			this.userStore.currBusiness.notification_sound = this.sounds[soundIndex];
			if(this.userStore.currBusiness.notification_sound !== "none") {

				let audio = (new Audio('https://img.qromo.io/audio/'+this.userStore.currBusiness.notification_sound+'.mp3'));
				audio.play().catch(() => {
					console.warn("Audio play not allowed");
				});
			} else {
				this.userStore.currBusiness.notification_sound = null;
			}
		},
		changeIntegration(integrationIndex) {
			this.userStore.currBusiness.integration_id = integrationIndex || null;
		},
		changedVat(v) {
			if(this.loadingVat) { return }
			let sanitizedVat = (v || '').replace(/[^0-9]/g, '').slice(0, 11);
			this.userStore.modBusinessInfos.vat = sanitizedVat;
			if(sanitizedVat && sanitizedVat?.length && ((sanitizedVat?.length >= 8 && COUNTRY !== 'IT') || (COUNTRY === 'IT' && sanitizedVat?.length >= 11))) {
				this.requestCompanyInfo();
			}
		},
		blurActiveElement() {
			if(document.activeElement) {
				document.activeElement.blur();
			}
		},
		requestCompanyInfo() {
			this.blurActiveElement();
			this.loadingVat = true;
			let errorCallback = (err) => {
				this.loadingVat = false;
				console.warn('Company vat fetch error: ', err)
			}
			API.init().getBusinessCompanyInfoFromVat(this.userStore.modBusinessInfos.vat, null, this.userStore.currBusinessId, (resp) => {
				if(resp && resp.result === "OK" && resp.data) {
					if(resp.data?.company) {
						let address = this.addressDataFromPlace(resp.data.geodecoded_address) || resp.data.company.address || '';
						this.userStore.modBusinessInfos.fiscal_name = resp.data.company.name || '';
						this.userStore.modBusinessInfos.fiscal_code = '';
						this.userStore.modBusinessInfos.sdi = resp.data.company.sdi || '0000000';
						this.changeBusinessLegalAddress(address)
						this.changeBusinessAddress(address)
					} else {
						errorCallback('Not found');
					}
				} else {
					errorCallback('Rate limit');
				}
				this.loadingVat = false;
			}, err=>{
				errorCallback(err);
			});
		},
		addressDataFromPlace(place) {
			let returningData = {};
			if(!place || !place?.address_components || !place?.address_components?.length || !place?.geometry || !place?.geometry?.location) {
				return '';
			}

			place.address_components.forEach(component => {

				if(component.types && component.types.length > 0) {
					if(component.types[0] === "administrative_area_level_1") {
						returningData.regione = component.long_name;
					} else if(component.types[0] === "administrative_area_level_2") {
						returningData.provincia_codice = component.short_name;
						returningData.provincia = component.long_name;
					} else if(component.types[0] === "administrative_area_level_3") {
						returningData.comune = component.long_name;
					} else if(component.types[0] === "route") {
						returningData.via = component.long_name;
					} else if(component.types[0] === "postal_code") {
						returningData.zip = component.long_name;
					} else if(component.types[0] === "street_number") {
						returningData.civico = component.long_name;
					}
				}

			});
			returningData.country = COUNTRY;
			return {
				address: place.formatted_address,
				data: returningData,
				lat: place.geometry.location.lat,
				lng: place.geometry.location.lng
			};
		},
	},
	mounted() {
		if(this.userStore.shouldOpenFiscal) {
			if(this.items && this.items.length > 0) {
				this.items[0].active = true;
			}
			this.userStore.shouldOpenFiscal = false;
		}
		this.userStore.resetModBusinessInfos();
		let finalAppearanceIndex = this.userStore.darkMode ? 1 : 0;
		this.appearanceIndex = Number(localStorage.getItem('darkMode') || 0) === 2 ? 2 : finalAppearanceIndex;
		if(this.userStore.shouldOpenBusinessInfo) {
			this.closeListDropdownHeaders();
			this.activeBusinessInfos = true;
			this.userStore.shouldOpenBusinessInfo = false;
		}
		if(this.userStore.shouldOpenPermissions) {
			this.clickListDropdownHeaderPermissions();
			this.activePermissions = true;
			this.userStore.shouldOpenPermissions = false;
		}
	},
	watch: {
		currBusinessId() {
			this.usersPermissions = [];
			if(this.activePermissions) {
				this.getUsersPermissions();
			}
			this.userStore.resetModBusinessInfos();
		},
		"userStore.shouldOpenBusinessInfo": function (newVal, oldVal) {
			if (!!newVal && newVal !== oldVal) {
				this.closeListDropdownHeaders();
				this.activeBusinessInfos = true;
				this.userStore.shouldOpenBusinessInfo = false;
			}
		},
		"userStore.shouldOpenPermissions": function (newVal, oldVal) {
			if (!!newVal && newVal !== oldVal) {
				this.clickListDropdownHeaderPermissions();
				this.activePermissions = true;
				this.userStore.shouldOpenPermissions = false;
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.card-margin {
	margin-top: var(--main-padding);
}
.list {
	:deep(.list-item .dropdown-container) {
		z-index: unset;
	}
	:deep(.list-item .dropdown-content) {
		background-color: $alpha_1;
	}
	:deep(.list-item .list-item-title) {
		max-width: calc(100% - 120px);
		@media (max-width: 600px) {
			font-size: 14px;
		}
		@media (max-width: 400px) {
			font-size: 12px;
		}
		@media (max-width: 360px) {
			max-width: 100%;
			.go-to-stripe-btn {
				margin-top: 10px;
			}
		}
	}
	&:deep(.list-item:first-of-type:before),
	&:deep(.list-item:last-of-type:after) {
		display: none;
	}
	:deep(.dropdown-header) {
		@include centered;
		@include noSelect;
		justify-content: space-between;
		cursor: pointer;
		padding: 6px 0;

		&.button-row {
			cursor: default;
			padding: 2px 0;
		}
		@media (max-width: 360px) {
			.go-to-stripe-btn {
				margin-top: 10px;
			}
		}
	}
}
.card:deep(.card-body) {
	padding: 0 !important;
}
.input-button-row {
	@include centered;
	justify-content: space-between;

	.input-wrapper, .select-wrapper {
		&:not(.single-input-wrapper) {
			margin-right: 20px;
		}
	}
	.add-user-input {
		margin-right: 0;
	}
	> p {
		font-size: 16px;
		max-width: calc(100% - 410px);
		span {
			font-size: 14px;
			line-height: 14px;
			color: #9B9B9B;
		}
	}
	&.select-row > p {
		font-size: 16px;
	}
	.input-btn-container {
		min-width: 410px;
	}
	@media (max-width: 768px) {
		&:not(.select-row) {
			justify-content: flex-end;

			.row:not(.vat-loader-row) {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.input-wrapper, .select-wrapper {
				margin-right: 0;
				max-width: 100%;
				&:not(.single-input-wrapper) {
					margin-bottom: 10px;
				}
			}
			.select-wrapper{ width: 100%; }
			> p {
				max-width: 100%;
				width: 100%;
				margin-bottom: 4px;
			}
		}
	}
	@media (max-width: 600px) {
		> p, &.select-row > p {
			font-size: 14px;
		}
		.input-btn-container {
			min-width: unset;
			.select-wrapper{ width: auto; }
		}
		> p {
			max-width: calc(100% - 310px);
		}
	}
	@media (max-width: 480px) {
		&.select-row {
			align-items: flex-start;
			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.select-wrapper {
				margin-right: 0;
				margin-bottom: 10px;
				max-width: unset;
				min-width: 200px;
			}
			> p {
				max-width: calc(100% - 180px);
				margin-bottom: 4px;
			}
		}

		.input-btn-container {
			min-width: 100%;
		}
	}
	@media (max-width: 400px) {
		> p, &.select-row > p {
			font-size: 12px;
		}
		.vat-loader-row {
			justify-content: flex-start;

			p {
				margin-right: 10px;
			}
		}
	}
	@media (max-width: 360px) {
		&.select-row {
			justify-content: flex-end;
			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.select-wrapper {
				max-width: 100%;
				width: 100%;
			}
			> p {
				max-width: 100%;
				width: 100%;
				margin-bottom: 4px;
			}
		}
	}
}
.business-info-container > .row {
	position: relative;
	padding: 6px 0;

	&:not(:last-of-type):after {
		@include pseudoLine(1px, 100%, 100%, 0, $alpha_4);
	}
	&:first-of-type {
		padding-top: 0;
	}
	&:last-of-type {
		padding-bottom: 0;
	}
}
.invoice-info-container > .row {
	position: relative;
	padding: 6px 0;

	&:not(:last-of-type):after {
		@include pseudoLine(1px, 100%, 100%, 0, $alpha_4);
	}
	&:first-of-type {
		padding-top: 0;
	}
	&:last-of-type {
		padding-bottom: 0;
	}
}
.ml-auto {
	margin-left: auto;
}
p.delete-info, p.integration-info {
	font-size: 14px;
	color: #9B9B9B;
}
.extra-text {
	p {
		font-size: 14px;
		color: #9B9B9B;
	}
}
.disconnected-step {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	justify-content: center;
	width: 100%;
	gap: 10px;
}
.input-button-row {
	.input-btn-container {
		.no-margin {
			:deep(.select-wrapper) {
				margin-right: 0;
			}
		}
	}
}
</style>